import type { OrderDto } from '@restaurant/shared';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog';
import { api } from '@/lib/api';
import { toastError } from '@/lib/errors';
import { OrderEditor, toEditorValue } from './OrderEditor';

/** Manual order taken from a chat. Created as confirmed, so the customer gets the confirmation right away. */
export function CreateOrderDialog({ conversationId, defaultAddress, onCreated }: { conversationId: string; defaultAddress?: string | null; onCreated?: (o: OrderDto) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline"><Plus /> New order</Button>
      </DialogTrigger>
      <DialogContent title="New order" description="Prices are taken from the menu. The customer is notified when you confirm." side="right">
        <OrderEditor
          initial={toEditorValue(undefined, defaultAddress)}
          submitLabel="Create & confirm"
          onSubmit={async (v) => {
            try {
              const o = await api.orders.create({
                conversationId,
                type: v.type,
                items: v.items.map((l) => ({ ...l, notes: l.notes ?? null })),
                address: v.type === 'delivery' ? v.address : null,
                notes: v.notes || null,
                paymentMethod: v.paymentMethod,
                confirm: true,
              });
              if (o.notification && !o.notification.sent) toast.warning(`Order #${o.number} created, but the customer was not notified`, { description: o.notification.reason });
              else toast.success(`Order #${o.number} confirmed`);
              onCreated?.(o);
              setOpen(false);
            } catch (err) {
              toastError(err);
            }
          }}
        />
      </DialogContent>
    </Dialog>
  );
}
